import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { LogIn, Play, UserPlus, X } from 'lucide-react';

interface AccountRequiredGateProps {
  isDark: boolean;
  open?: boolean;
  variant?: 'modal' | 'page';
  onClose?: () => void;
}

function GateContent({
  isDark,
  onClose,
  variant,
}: Pick<AccountRequiredGateProps, 'isDark' | 'onClose' | 'variant'>) {
  const isPage = variant === 'page';
  const panel = isDark
    ? 'border-white/10 bg-[#1b1b1d] text-white shadow-[0_24px_70px_rgba(0,0,0,0.48)]'
    : 'border-black/10 bg-white text-neutral-950 shadow-[0_24px_70px_rgba(0,0,0,0.16)]';
  const muted = isDark ? 'text-white/60' : 'text-neutral-600';
  const primary = isDark
    ? 'bg-white text-neutral-950 hover:bg-white/90'
    : 'bg-neutral-900 text-white hover:bg-neutral-800';
  const secondary = isDark
    ? 'border-white/15 text-white/85 hover:bg-white/[0.06]'
    : 'border-black/10 text-neutral-800 hover:bg-black/[0.035]';

  return (
    <section
      className={`relative w-full max-w-md rounded-[26px] border p-6 backdrop-blur-2xl sm:p-8 ${panel}`}
      aria-labelledby="account-required-title"
      aria-describedby="account-required-description"
    >
      {!isPage && onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close account sign-in message"
          className={`absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full ${
            isDark ? 'text-white/50 hover:bg-white/[0.07]' : 'text-neutral-500 hover:bg-black/[0.05]'
          }`}
        >
          <X size={18} aria-hidden="true" />
        </button>
      )}

      <span className={`grid h-12 w-12 place-items-center rounded-2xl ${
        isDark ? 'bg-white/[0.07] text-white' : 'bg-black/[0.045] text-neutral-900'
      }`}>
        <Play size={20} fill="currentColor" strokeWidth={0} aria-hidden="true" />
      </span>
      <h2 id="account-required-title" className="mt-5 text-xl font-semibold tracking-[-0.035em] sm:text-2xl">
        Create an account to generate lessons
      </h2>
      <p id="account-required-description" className={`mt-2 text-sm leading-6 ${muted}`}>
        Create a free LAVYN account or log in to generate video lessons and save them to your History.
        Guests cannot generate videos, and no advertisements are shown to guests.
      </p>

      <div className="mt-6 grid gap-2 sm:grid-cols-2">
        <Link
          to="/auth/signup"
          onClick={onClose}
          className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl px-4 text-sm font-semibold transition-colors ${primary}`}
        >
          <UserPlus size={17} aria-hidden="true" />
          Sign Up
        </Link>
        <Link
          to="/auth/login"
          onClick={onClose}
          className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border px-4 text-sm font-semibold transition-colors ${secondary}`}
        >
          <LogIn size={17} aria-hidden="true" />
          Log In
        </Link>
      </div>

      {isPage && onClose && (
        <button
          type="button"
          onClick={onClose}
          className={`mt-4 min-h-11 w-full rounded-xl text-sm font-medium ${muted}`}
        >
          Back to LAVYN
        </button>
      )}
    </section>
  );
}

export default function AccountRequiredGate({
  isDark,
  open = false,
  variant = 'modal',
  onClose,
}: AccountRequiredGateProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (variant !== 'modal') return;
    const dialog = dialogRef.current;
    if (open && dialog && !dialog.open) dialog.showModal();
    else if (!open && dialog?.open) dialog.close();
  }, [open, variant]);

  if (variant === 'page') {
    return (
      <main className={`flex min-h-full items-center justify-center px-5 py-12 ${
        isDark ? 'bg-[#0f0f10]' : 'bg-neutral-50'
      }`}>
        <GateContent isDark={isDark} onClose={onClose} variant="page" />
      </main>
    );
  }

  return (
    <dialog
      ref={dialogRef}
      aria-modal="true"
      className={`m-auto w-[min(440px,calc(100%-28px))] max-w-none rounded-[26px] border-0 bg-transparent p-0 text-inherit backdrop:bg-black/65 backdrop:backdrop-blur-sm ${
        isDark ? 'text-white' : 'text-neutral-950'
      }`}
      onCancel={(event) => {
        event.preventDefault();
        onClose?.();
      }}
      onClose={() => {
        if (open) onClose?.();
      }}
    >
      <GateContent isDark={isDark} onClose={onClose} variant="modal" />
    </dialog>
  );
}
